import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown, Phone, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type NavLinkItem = {
  label: string
  href: string
  highlight?: boolean
  megaMenu?: { icon: string; label: string; href: string }[]
}

const NAV_LINKS: NavLinkItem[] = [
  {
    label: 'Services',
    href: '/services',
    megaMenu: [
      { icon: '🧠', label: 'Occupational Therapy', href: '/services/occupational-therapy' },
      { icon: '🗣️', label: 'Speech & Language Therapy', href: '/services/speech-language-therapy' },
      { icon: '🏃', label: 'Physiotherapy', href: '/services/physiotherapy' },
      { icon: '👁️', label: 'Sensory Integration', href: '/services/sensory-integration' },
      { icon: '💂', label: 'ABA Therapy', href: '/services/aba-therapy' },
      { icon: '📚', label: 'Special Education', href: '/services/special-education' },
      { icon: '👂', label: 'Hearing Aid & Audiology', href: '/services/hearing-aid' },
      { icon: '🧐', label: 'Autism Treatment', href: '/services/autism-treatment' },
    ],
  },
  {
    label: 'Conditions',
    href: '/conditions',
    megaMenu: [
      { icon: '🔵', label: 'Autism Spectrum', href: '/conditions/autism' },
      { icon: '🟡', label: 'ADHD', href: '/conditions/adhd' },
      { icon: '🟢', label: 'Cerebral Palsy', href: '/conditions/cerebral-palsy' },
      { icon: '🟠', label: 'Down Syndrome', href: '/conditions/down-syndrome' },
      { icon: '🔴', label: 'Developmental Delays', href: '/conditions/developmental-delays' },
      { icon: '🟣', label: 'Learning Disabilities', href: '/conditions/learning-disabilities' },
    ],
  },
  { label: 'Our Team', href: '/team' },
  { label: 'Branches', href: '/branches' },
  {
    label: 'Free Tools',
    href: '/screening',
    highlight: true,
    megaMenu: [
      { icon: '🧪', label: 'Free Child Screening', href: '/screening' },
      { icon: '🩺', label: 'Symptom Triage (AI)', href: '/triage' },
      { icon: '📅', label: 'Weekly Activity Plan (AI)', href: '/activity-plan' },
      { icon: '💬', label: 'Ask BRC Chatbot', href: '/contact' },
    ],
  },
  {
    label: 'More',
    href: '/about',
    megaMenu: [
      { icon: '🏢', label: 'About BRC', href: '/about' },
      { icon: '📝', label: 'Blog & Resources', href: '/blog' },
      { icon: '🛡️', label: 'Insurance & Billing', href: '/insurance' },
      { icon: '🩺', label: 'For Physicians', href: '/for-physicians' },
      { icon: '📞', label: 'Contact Us', href: '/contact' },
    ],
  },
]

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [menuTimeout, setMenuTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Close on route change
  useEffect(() => {
    setActiveMenu(null)
    setMobileOpen(false)
  }, [location.pathname])

  // Simple scroll lock
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  // Close on Escape
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  function handleMouseEnter(label: string) {
    if (menuTimeout) clearTimeout(menuTimeout)
    setActiveMenu(label)
  }

  function handleMouseLeave() {
    const t = setTimeout(() => setActiveMenu(null), 120)
    setMenuTimeout(t)
  }

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <header
        className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl shadow-navbar border-b border-border/60"
        style={{ height: 'var(--header-height)' }}
      >
        <div className="container mx-auto h-full flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0 group" aria-label="BRC Home">
            <div className="rounded-xl overflow-hidden bg-white shadow-sm">
              <img
                src="/images/brc-logo.webp"
                alt="Brainchild Rehab Centre"
                className="h-12 w-auto object-contain px-2 py-1 transition-transform group-hover:scale-105"
              />
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.megaMenu && handleMouseEnter(link.label)}
                onMouseLeave={handleMouseLeave}
              >
                <NavLink
                  to={link.href}
                  className={({ isActive }) => cn(
                    'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                    link.highlight
                      ? isActive
                        ? 'text-white bg-emerald-600 shadow-sm'
                        : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200'
                      : isActive
                        ? 'text-primary bg-primary/10'
                        : 'text-foreground hover:text-primary hover:bg-primary/8'
                  )}
                >
                  {link.label}
                  {link.megaMenu && (
                    <ChevronDown
                      className={cn('w-3.5 h-3.5 transition-transform duration-200',
                        activeMenu === link.label && 'rotate-180')}
                    />
                  )}
                </NavLink>

                {/* Mega menu */}
                {link.megaMenu && (
                  <AnimatePresence>
                    {activeMenu === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.97 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-2xl shadow-modal border border-border/50 p-3 grid grid-cols-2 gap-1"
                        onMouseEnter={() => handleMouseEnter(link.label)}
                        onMouseLeave={handleMouseLeave}
                      >
                        {link.megaMenu.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-primary/8 group transition-colors"
                            onClick={() => setActiveMenu(null)}
                          >
                            <span className="text-lg">{item.icon}</span>
                            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors leading-tight">
                              {item.label}
                            </span>
                          </Link>
                        ))}
                        <Link
                          to={link.href}
                          className="col-span-2 mt-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-primary/8 text-primary text-sm font-semibold hover:bg-primary/15 transition-colors"
                          onClick={() => setActiveMenu(null)}
                        >
                          View All {link.label} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Phone */}
            <a
              href="tel:+919655488554"
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-primary hover:bg-primary/8 transition-all"
              aria-label="Call BRC"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">+91 96554 88554</span>
            </a>

            {/* Book Now CTA */}
            <button
              onClick={() => navigate('/book')}
              className="btn btn-accent btn-sm shadow-button"
            >
              Book Now
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg text-foreground hover:bg-muted transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu — portaled to body; always mounted and slid via `animate`
          (no AnimatePresence exit, which could stall during a route change and
          leave the menu stuck open on the next page). */}
      {createPortal(
        <>
          {/* Backdrop — CSS opacity transition (GPU; never starved by heavy page renders) */}
          <div
            className={cn(
              'fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300',
              mobileOpen ? 'opacity-100' : 'opacity-0'
            )}
            style={{ zIndex: 2147483646, pointerEvents: mobileOpen ? 'auto' : 'none' }}
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Slide-in panel — CSS transform transition, driven purely by mobileOpen.
              Compositor-animated so it slides out reliably even mid-navigation. */}
          <nav
            className={cn(
              'fixed right-0 top-0 bottom-0 w-[min(320px,100vw)] bg-white shadow-modal lg:hidden flex flex-col transition-transform duration-300 ease-out will-change-transform',
              mobileOpen ? 'translate-x-0' : 'translate-x-full'
            )}
            style={{ zIndex: 2147483647, paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', pointerEvents: mobileOpen ? 'auto' : 'none' }}
            aria-label="Mobile navigation"
            aria-hidden={!mobileOpen}
          >
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-gradient-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-extrabold">B</span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-primary text-sm truncate">Brainchild Rehab Centre</div>
                      <div className="text-xs text-muted-foreground truncate">Disability to Ability</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="ml-2 flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6" strokeWidth={2.5} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-1">
                  {NAV_LINKS.map((link) => (
                    <MobileNavItem
                      key={link.label}
                      link={link}
                      onClose={() => setMobileOpen(false)}
                    />
                  ))}
                </div>

                <div className="p-4 border-t border-border space-y-3">
                  <a href="tel:+919655488554" className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4 text-primary" />
                    +91 96554 88554
                  </a>
                  <Link
                    to="/book"
                    className="btn btn-accent btn-md w-full justify-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    Book Appointment
                  </Link>
                </div>
          </nav>
        </>,
        document.body
      )}
    </>
  )
}

function MobileNavItem({ link, onClose }: { link: NavLinkItem; onClose: () => void }) {
  const [open, setOpen] = useState(false)

  if (!link.megaMenu) {
    return (
      <NavLink
        to={link.href}
        onClick={onClose}
        className={({ isActive }) =>
          cn(
            'flex items-center px-4 py-3 rounded-xl text-sm font-semibold transition-colors',
            isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
          )
        }
      >
        {link.label}
      </NavLink>
    )
  }

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-colors',
          link.highlight ? 'text-emerald-700 bg-emerald-50' : 'text-foreground hover:bg-muted'
        )}
      >
        {link.label}
        <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'clip' }}
          >
            <div className="pl-4 pb-2 grid grid-cols-2 gap-1">
              {link.megaMenu?.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-primary/8 text-sm text-foreground hover:text-primary transition-colors"
                >
                  <span>{item.icon}</span>
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
